import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { quickActionsForRole } from "@/lib/quickActions";

export default function QuickActionsFab() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const actions = quickActionsForRole(user?.role);
    const primary = actions.find((a) => a.primary) || actions[0];
    const rest = actions.filter((a) => a.id !== primary?.id);

    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("pointerdown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (actions.length === 0) return null;

    const go = (to) => {
        setOpen(false);
        navigate(to);
    };

    return (
        <div ref={rootRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3" data-testid="quick-actions-fab">
            {open && (
                <div className="w-52 overflow-hidden rounded-xl border border-[#E8E4DE] bg-white shadow-[0_12px_40px_rgba(45,41,38,0.14)]">
                    <p className="border-b border-[#E8E4DE] bg-[#FFFEF9] px-3 py-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                        Shortcuts
                    </p>
                    <div className="p-2">
                        {primary && (
                            <button
                                type="button"
                                onClick={() => go(primary.to)}
                                className="mb-1 flex w-full items-center gap-3 rounded-lg bg-[#4A0E17] px-3 py-2.5 text-left text-sm font-semibold text-white transition-colors hover:bg-[#3a0b12]"
                                data-testid={`quick-action-${primary.id}`}
                            >
                                <primary.icon className="size-4 shrink-0" />
                                {primary.label}
                            </button>
                        )}
                        {rest.map((action) => (
                            <button
                                key={action.id}
                                type="button"
                                onClick={() => go(action.to)}
                                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-[#3D2817] transition-colors hover:bg-[#FFFEF9]"
                                data-testid={`quick-action-${action.id}`}
                            >
                                <action.icon className="size-4 shrink-0 text-[#D4AF37]" />
                                {action.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex size-14 items-center justify-center rounded-full bg-[#4A0E17] text-white shadow-[0_8px_24px_rgba(74,14,23,0.35)] transition-transform hover:scale-105 hover:bg-[#3a0b12]"
                aria-label={open ? "Close quick actions" : "Open quick actions"}
                aria-expanded={open}
                data-testid="quick-actions-toggle"
            >
                {open ? <X className="size-6" /> : <Plus className="size-6" />}
            </button>
        </div>
    );
}
